import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const BREAKPOINT = 'md';

interface Props {
  id: string;
  href?: string;
  icon?: SvgIconComponent;
  title: string;
  details?: string[];
  button?: {
    onClick?: () => void;
    content: React.ReactNode;
  };
  onClick?: () => void;
  toggleOpenModal: () => void;
}

function SearchModalRow({
  id,
  href,
  button,
  icon,
  title,
  details,
  onClick,
  toggleOpenModal,
}: Props) {
  const theme = useTheme();

  const Icon = icon;
  const component = href ? Link : button ? ButtonBase : 'div';

  const onItemClicked = () => {
    if (onClick) {
      onClick();
    } else if (button?.onClick) {
      button.onClick();
    }
    toggleOpenModal();
  };

  return (
    <Box
      px={0.5}
      fontFamily={theme.typography.fontFamily}
      boxSizing="border-box"
      to={href}
      onClick={onItemClicked}
      component={component}
      key={id}
      display="flex"
      flexDirection="row"
      alignItems="center"
      gap={2}
      py={button ? 1.5 : 2.5}
      width="100%"
      borderBottom={`1px solid ${theme.palette.darkerLightGrey.main}`}
      sx={{
        // Rows with an href render as <a>; keep them off the UA link blue.
        color: 'text.primary',
        textDecoration: 'none',
        ':hover': {
          bgcolor: theme.palette.lightGrey.main,
        },
      }}>
      {Icon && (
        <Icon
          sx={{
            color: 'text.primary',
            fontSize: '24px',
          }}
        />
      )}
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={{
          xs: 1,
          [BREAKPOINT]: 2,
        }}>
        <Typography
          variant="h5"
          component="p"
          noWrap
          width={{
            xs: '100%',
            md: 'auto',
          }}>
          {title}
        </Typography>
        {!!details?.length && (
          <Stack
            boxSizing="border-box"
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            divider={
              <Typography
                variant="inherit"
                component="span"
                color="text.primary">
                &#x2022;
              </Typography>
            }>
            {details.map(detail => (
              <Typography
                key={`${id}-${detail}`}
                variant="body2"
                color="text.primary">
                {detail}
              </Typography>
            ))}
          </Stack>
        )}
      </Box>

      {button && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={1}
          borderRadius="10px"
          ml="auto"
          sx={{
            ':hover': {
              bgcolor: '#ffffff',
            },
          }}>
          {button.content}
        </Box>
      )}
    </Box>
  );
}

export default SearchModalRow;
